import {
  adminApiClient,
  extractDownloadFilename,
  resolveApiErrorMessage,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type {
  ApiResponse,
  MerchantSignupAccessEntry,
  MerchantSignupAccessGenerateResponse,
  MerchantSignupAccessImportResponse,
  MerchantSignupAccessListResponse,
  SignupEntryType,
} from '@/types'

export interface MerchantSignupAccessListParams {
  entryType?: SignupEntryType
  status?: 'active' | 'disabled'
  limit?: number
  offset?: number
}

export interface MerchantSignupAccessCreatePayload {
  entryType: SignupEntryType
  value: string
  label?: string | null
  status?: 'active' | 'disabled'
  expiresAt?: string | null
}

export interface MerchantSignupAccessUpdatePayload {
  value?: string
  label?: string | null
  status?: 'active' | 'disabled'
  expiresAt?: string | null
}

export interface MerchantSignupAccessImportPayload {
  entryType: SignupEntryType
  values: string
  status?: 'active' | 'disabled'
  expiresAt?: string | null
}

export interface MerchantSignupAccessGeneratePayload {
  count?: number
}

export const merchantSignupInviteService = {
  async list(
    params?: MerchantSignupAccessListParams,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessListResponse> {
    const response = await adminApiClient.get<ApiResponse<MerchantSignupAccessListResponse>>(
      '/admin/system/merchant-signup-invites',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )

    return response?.data?.data ?? { entries: [], total: 0, limit: 100, offset: 0 }
  },

  async create(
    payload: MerchantSignupAccessCreatePayload,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessEntry> {
    try {
      const response = await adminApiClient.post<ApiResponse<MerchantSignupAccessEntry>>(
        '/admin/system/merchant-signup-invites',
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to create signup entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to create signup entry'))
    }
  },

  async update(
    entryId: string,
    payload: MerchantSignupAccessUpdatePayload,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessEntry> {
    try {
      const response = await adminApiClient.patch<ApiResponse<MerchantSignupAccessEntry>>(
        `/admin/system/merchant-signup-invites/${encodeURIComponent(entryId)}`,
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to update signup entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to update signup entry'))
    }
  },

  async delete(
    entryId: string,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessEntry> {
    try {
      const response = await adminApiClient.delete<ApiResponse<MerchantSignupAccessEntry>>(
        `/admin/system/merchant-signup-invites/${encodeURIComponent(entryId)}`,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to delete signup entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to delete signup entry'))
    }
  },

  async import(
    payload: MerchantSignupAccessImportPayload,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessImportResponse> {
    try {
      const response = await adminApiClient.post<ApiResponse<MerchantSignupAccessImportResponse>>(
        '/admin/system/merchant-signup-invites/import',
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to import signup entries')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to import signup entries'))
    }
  },

  async generate(
    payload: MerchantSignupAccessGeneratePayload,
    mode?: NetworkEnvironment
  ): Promise<MerchantSignupAccessGenerateResponse> {
    try {
      const response = await adminApiClient.post<ApiResponse<MerchantSignupAccessGenerateResponse>>(
        '/admin/system/merchant-signup-invites/generate',
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to generate signup invite codes')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to generate signup invite codes'))
    }
  },

  async exportCsv(
    params?: MerchantSignupAccessListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>(
        '/admin/system/merchant-signup-invites/export',
        {
          params,
          responseType: 'blob',
          ...buildModeAwareConfig(mode),
        }
      )
      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `signup-entries-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export signup entries'))
    }
  },
}
